import "./ProductSearch.scss";
import axios from "axios";
import { toast } from 'react-toastify';
import { useState, useEffect } from "react";
import { Table } from "@merchstores/shared/elements/Table";
import { Checkbox } from "@merchstores/shared/elements/Checkbox";
import { Logo } from "@merchstores/shared/elements/Logo";
import { CTA } from "@merchstores/shared/elements/Cta";
import Modal from "react-responsive-modal";
import { SearchBar } from "@merchstores/shared/elements/SearchBar";
import { useContext } from "react";
import { Context } from "@merchstores/admin/context";

const MIN_STOCK = 20;

interface IProductSearchProps {
  open: boolean,
  onClose?(): void,
  storeCode: string,
  storeSubdomain: string,
  onProductsAdded?(product: any): void,
  disabledProductIds: string[],
}

interface IProduct {
  id: string,
  handle: string,
  image: string,
  title: string,
  totalInventory: number,
}

export const ProductSearch: React.FC<IProductSearchProps> = (props: IProductSearchProps) => {
  const { userRole } = useContext(Context);
  const [productsList, setProductsList] = useState(undefined);
  const [productsTableData, setProductsTableData] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const fetchProducts = async (queryValue: string) => {
    axios.post('/.netlify/functions/fetchBaseProducts', {
      query: queryValue,
    })
      .then(res => {
        if (res.data && res.data.length > 0) {
          setProductsList(res.data);
        } else {
          console.log('No base products found.');
        }
      })
      .catch(err => {
        console.error('fetchBaseProducts error:', err);
      })
  }

  const addProducts = () => {
    const selectedProducts = getSelectedProducts();

    setLoading(true);
    const selectedProductIds: string[] = [];
    const selectedProductTitles: string[] = [];
    selectedProducts.forEach((product: string) => {
      const productSplit = product.split('_');
      selectedProductIds.push(productSplit[0]);
      selectedProductTitles.push(atob(productSplit[1]));
    });
    axios.post('/.netlify/functions/cloneProducts', {
      productIds: selectedProductIds,
      productTitles: selectedProductTitles,
      subdomain: props.storeSubdomain,
      storeCode: props.storeCode,
    })
      .then((data: any) => {
        toast.success('The products have been successfully added to your store, but it may take a few minutes for them to appear in your product list.');
        localStorage.setItem('productsSelected', JSON.stringify([]));
        if (props.onClose) {
          props.onClose();
        }
        if (props.onProductsAdded) {
          props.onProductsAdded(data.data);
        }
      })
      .catch(err => {
        console.error('cloneProducts error:', err);
        toast.error('Error adding products');
      })
      .finally(() => {
        setLoading(false);
      })
  }

  const getSelectedProducts = () => {
    const currentSelectionString = localStorage.getItem('productsSelected');
    if (currentSelectionString) {
      return JSON.parse(currentSelectionString);
    }
    else {
      return [];
    }
  }

  const setSelectedCheckboxes = () => {
    const selectedProducts = getSelectedProducts();
    const checkboxDivs = document.querySelectorAll("div.product-checkbox") as NodeListOf<HTMLDivElement>;
    checkboxDivs.forEach(div => {
      const input = div.querySelector("input.product-checkbox") as HTMLInputElement;
      const svg = div.querySelector("svg");
      const inputValueSplit = input.value.split('-');
      if (selectedProducts.includes(inputValueSplit[0])) {
        svg?.classList.add('visible');
        svg?.classList.remove('invisible');
      }
    });
  }

  const onSearchChange = (query: string) => {
    fetchProducts(query);
  }

  const onProductCheckboxChange = (newValue: boolean, id: string) => {
    let currentSelection = getSelectedProducts();
    if (newValue) {
      currentSelection.push(id);
    }
    else {
      currentSelection = currentSelection.filter((productId: string) => productId !== id);
    }
    localStorage.setItem('productsSelected', JSON.stringify(currentSelection));
  }

  const onModalClose = () => {
    localStorage.setItem('productsSelected', JSON.stringify([]));
    if (props.onClose) {
      props.onClose();
    }
  }

  useEffect(() => {
    fetchProducts('');
  }, [userRole])

  const afterTableRender = () => {
    setSelectedCheckboxes();
  }

  useEffect(() => {
    if (productsList) {

      const productsTableData = productsList.map((product: IProduct) => {

        const imageSize = "_40x40";
        const position = product.image.lastIndexOf(".");
        const resizedProductImage = [product.image.slice(0, position), imageSize, product.image.slice(position)].join('');

        const productDisabled = (props.disabledProductIds ? props.disabledProductIds.includes(product.id) : false) || product.totalInventory <= MIN_STOCK;
        return {
          tr: {
            class: "",
            data: {
              productSelector: {
                desktopOnly: false,
                value: <Checkbox
                  id={`${product.id}_${btoa(product.title)}`}
                  className={'product-checkbox'}
                  onChange={onProductCheckboxChange}
                  disabled={productDisabled}
                />
              },
              productImage: {
                desktopOnly: false,
                value: <Logo classes="product-thumbnail" imgUrl={resizedProductImage} />
              },
              productTitle: {
                desktopOnly: false,
                value: <div className={productDisabled ? 'opacity-50' : ''}>{product.title}</div>
              },
              productStock: {
                desktopOnly: true,
                value: product.totalInventory <= MIN_STOCK ? <div className='product-out-of-stock mr-10'>OUT OF STOCK</div> : ''
              },
            }
          }
        }
      });
      setProductsTableData(productsTableData);
    }
  }, [productsList, props.disabledProductIds])

  return (
    <Modal open={props.open} onClose={onModalClose} center classNames={{ modal: "product-search-modal" }}>
      <h2>Add Products</h2>
      <div>
        <div>
          <SearchBar placeholder="Product Search" onChange={onSearchChange} />
        </div>

        {productsTableData ?
          <>
            {productsTableData.length > 0 &&
              <>
                {/* SELECT ALL BUTTON FUNCTIONALITY, NOT IN DESIGNS, BUT KEEPING IN CASE NEEDED */}

                {/* <div className="flex mb-10 h-30">
                  <Checkbox isSelected={selectedProducts && selectedProducts.length > 0} label={selectedProducts && selectedProducts.length > 0 ? `${selectedProducts.length} selected` : 'Select all'} className="member-checkbox items-center" onChange={selectAllChanged} />
                </div> */}
              </>
            }
            <div className='product-search-list-container'>
              <Table name="member-list" filter={true} data={productsTableData} itemsName="products" displayFooter={false} usePagination={false} afterRender={afterTableRender} />
            </div>
          </>
          : ''
        }
      </div>
      <div className="block md:hidden my-5">
        <CTA disabled={loading} formSubmit={false} size={'standard'} type={'primary'} classes="product-add-button mb-3" onClick={addProducts}>{!loading ? `Add Products` : 'Adding...'}</CTA>
        <CTA formSubmit={false} size={'standard'} type={'secondary'} classes="product-cancel-button" onClick={onModalClose}>Cancel</CTA>
      </div>
      <div className="hidden md:flex justify-between m-5">
        <CTA formSubmit={false} size={'standard'} type={'secondary'} classes="product-cancel-button" onClick={onModalClose}>Cancel</CTA>
        <CTA disabled={loading} formSubmit={false} size={'standard'} type={'primary'} classes="product-add-button" onClick={addProducts}>{!loading ? `Add Products` : 'Adding...'}</CTA>
      </div>
    </Modal>
  )
}
